import {
  scaleByDecimals,
  priceToWeiString,
  daysToSeconds,
  percentToBips,
} from './wei-scaling'
import type {
  EquityFormData,
  CuratorProof,
  CustomReferralWallet,
} from './equity-form-defaults'

export const EQUITY_CONTRACT_VERSION = 'v0.9.6'
export const EQUITY_PROTOCOL_FEE_BIPS = 200
export const EQUITY_ROYALTY_RECEIVER =
  '0x91e2304f860914348d7b1b1a91cf1cc3d3cf3211'

const toNumberOrZero = (s: string): number => {
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

const toIntOrZero = (s: string): number => Math.floor(toNumberOrZero(s))

const isoFromLocal = (local: string): string => {
  if (!local) return ''
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

const unixSecondsFromLocal = (local: string): number => {
  if (!local) return 0
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? 0 : Math.floor(d.getTime() / 1000)
}

const buildReferral = (form: EquityFormData) => {
  if (!form.referralEnabled) {
    return {
      enabled: false,
      general: { discount: 0, kickback: 0 },
      customWallets: [],
    }
  }
  return {
    enabled: true,
    general: {
      discount: toNumberOrZero(form.referralGeneralDiscount),
      kickback: toNumberOrZero(form.referralGeneralKickback),
    },
    customWallets: form.referralCustomWallets.map(
      (w: CustomReferralWallet) => ({
        walletAddress: w.walletAddress,
        telegram: w.telegram,
        promoCode: w.promoCode,
        discountPercentage: toNumberOrZero(w.discountPercentage),
        kickbackPercentage: toNumberOrZero(w.kickbackPercentage),
      }),
    ),
  }
}

export const mapEquityFormToPayload = (form: EquityFormData) => {
  const paymentDecimals = toIntOrZero(form.paymentToken.decimals)
  const projectDecimals = toIntOrZero(form.projectToken.decimals)

  const overview = {
    projectName: form.projectName,
    projectDescription: form.projectDescription,
    projectMemo: form.projectMemo,
    logoUrl: form.logoUrl,
    categoryTags: form.categoryTags,
    urls: {
      website: form.websiteUrl,
      x: form.xUrl,
      discord: form.discordUrl,
      other: form.otherUrls.map((u) => ({ url: u.url, type: u.type })),
    },
    pitchDeck: { type: 'link', value: form.pitchDeckUrl },
    researchReportUrl: form.researchReportUrl,
    requestResearchSupport: form.requestResearchSupport,
    dealType: form.dealType,
    jurisdiction: form.jurisdiction,
    targetRaise: form.targetRaise,
    fdv: form.fdv,
  }

  const detail = {
    minRaiseThreshold: toIntOrZero(form.minRaiseThreshold),
    minInvestmentPerUser: toIntOrZero(form.minInvestmentPerUser),
    useMaxDeposit: form.useMaxDeposit,
    ...(form.useMaxDeposit ? { maxDeposit: toIntOrZero(form.maxDeposit) } : {}),
    withdrawalType: 2,
    whitelist: [],
    depositChainId: toIntOrZero(form.depositChainId),
    paymentToken: {
      address: form.paymentToken.address,
      symbol: form.paymentToken.symbol,
      name: form.paymentToken.name,
      decimals: paymentDecimals,
    },
    distributionChainId: toIntOrZero(form.distributionChainId),
    projectToken: {
      address: form.projectToken.address,
      symbol: form.projectToken.symbol,
      name: form.projectToken.name,
      decimals: projectDecimals,
    },
    referral: buildReferral(form),
    accessoryServices: {
      marketing: form.accessoryMarketing,
      kolCampaigns: form.accessoryKolCampaigns,
      marketMaking: form.accessoryMarketMaking,
      treasuryManagement: form.accessoryTreasuryManagement,
      otcDeals: form.accessoryOtcDeals,
      auditing: form.accessoryAuditing,
      hrRecruitment: form.accessoryHrRecruitment,
      cexListing: form.accessoryCexListing,
      other: form.accessoryOther,
    },
    royaltySplit: {
      projectBips: toIntOrZero(form.projectRoyaltyBips),
      curatorBips: toIntOrZero(form.curatorRoyaltyBips),
    },
    pricingBlob: {
      schemaId: 0,
      version: 1,
      whitelist: { root: '0x' },
      payload: {
        fdvInWei: scaleByDecimals(form.fdv, projectDecimals),
        maxCapInWei: scaleByDecimals(form.maxCap, paymentDecimals),
        tokenPriceInWei: priceToWeiString(form.tokenPrice, paymentDecimals),
        cliffSecs: daysToSeconds(toNumberOrZero(form.cliffDays)),
        vestingSecs: daysToSeconds(toNumberOrZero(form.vestingDays)),
        tgeUnlockBips: percentToBips(toNumberOrZero(form.tgeUnlockPct)),
      },
    },
    referralDiscount: form.referralEnabled
      ? toNumberOrZero(form.referralDiscount)
      : 0,
  }

  const funds = {
    projectReceivingWallet: form.projectReceivingWallet,
    curatorCommitmentAmount: scaleByDecimals(
      form.curatorCommitmentAmount,
      paymentDecimals,
    ),
    curatorProofs: form.curatorProofs.map((p: CuratorProof) => ({
      type: p.type,
      value: p.value,
    })),
    curatorFeeBips: toIntOrZero(form.curatorFeeBips),
    curatorWallet: form.curatorWallet,
    protocolFeeBips: EQUITY_PROTOCOL_FEE_BIPS,
  }

  const timelines = [
    { type: 'depositStarts', datetime: isoFromLocal(form.depositStarts) },
    { type: 'depositEnds', datetime: isoFromLocal(form.depositEnds) },
    {
      type: 'shortlistNotified',
      datetime: isoFromLocal(form.shortlistNotified),
    },
    { type: 'signAgreement', datetime: isoFromLocal(form.signAgreement) },
    { type: 'firstUnlock', datetime: isoFromLocal(form.firstUnlock) },
    { type: 'nftTransferable', datetime: isoFromLocal(form.nftTransferable) },
  ]

  const tgeTimestamp = unixSecondsFromLocal(form.firstUnlock)
  const nonTransferableUntil = unixSecondsFromLocal(form.nftTransferable)
  const totalRoyaltyBips =
    toIntOrZero(form.projectRoyaltyBips) + toIntOrZero(form.curatorRoyaltyBips)

  const nftMetadata = {
    name: form.nftName,
    symbol: form.nftSymbol,
    tgeTimestamp,
    nonTransferableUntil,
    royaltyReceiver: EQUITY_ROYALTY_RECEIVER,
    royaltyBips: totalRoyaltyBips,
    royaltySplit: {
      projectBips: toIntOrZero(form.projectRoyaltyBips),
      curatorBips: toIntOrZero(form.curatorRoyaltyBips),
    },
  }

  const visibilityEligibility = {
    visibility: form.visibility,
    blockedCountries: form.blockedCountries,
    investorEligibility: form.investorEligibility,
  }

  return {
    type: 2,
    metadata: {
      overview,
      detail,
      funds,
      visibilityEligibility,
      timelines,
      nftMetadata,
      isEquity: true,
      equityVehicle: {
        companyName: form.vehicleCompanyName,
        directorName: form.vehicleDirectorName,
        signatoryName: form.vehicleSignatoryName,
        signatoryTitle: form.vehicleSignatoryTitle,
        registeredAddress: form.vehicleRegisteredAddress,
        companyNo: form.vehicleCompanyNo,
      },
      equityBasePdfKeys: form.equityBasePdfs.map((p) => ({
        name: p.name,
        key: p.key,
      })),
    },
    is_visible: true,
    approved: false,
    contract_version: EQUITY_CONTRACT_VERSION,
  }
}
